import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { api, parseApiError, type CompStage, type SwissScoreboardPairing } from '../lib/api';
import { displayClanName } from '../lib/displayName';
import { HelpText } from './HelpText';
import { StatusBadge } from './StatusBadge';
import { ResultEntryForm } from './ResultEntryForm';

// ─────────────────────────────────────────────────────────────────────────────
// SwissRoundsPanel — E1 (comp-lifecycle-pipeline, Swiss rounds panel): the
// steering wheel for a LIVE SWISS stage. Rendered INSIDE StagePipelinePanel's
// SWISS stage card, modeled on SeedingRoundsPanel's structure (round tabs,
// inline result entry reused verbatim, a "generate next" action) but over the
// Swiss engine's own shape — record-bucketed pairings, Buchholz, and an
// IN/ACTIVE/OUT cut instead of a flat points table.
//
// Round 1 is already generated at Start (the existing startCompStage call —
// nothing new there, see generateSwissPlay in portalApi/index.ts). Rounds 2+
// run through the LEGACY (non-stage-scoped) /comps/:compId/swiss/pair and
// /swiss/complete-round routes — the stage pipeline's own /start only ever
// generates round 1 (see api.ts's completeSwissRound/pairSwissRound doc
// comment) — this panel is the first UI surface that ever drove them; their
// own refusals (round not complete, stage already complete) ARE the guard,
// surfaced verbatim rather than re-validated client-side.
//
// Standings are typography-only (house doctrine: no severity-colour
// highlight strips) — W-L, Buchholz, and state read as plain text, never a
// colour-coded pill.
// ─────────────────────────────────────────────────────────────────────────────

export function SwissRoundsPanel({ compId, stage }: { compId: string; stage: CompStage }) {
  const qc = useQueryClient();
  const stageId = stage.stageId;

  const scoreboardKey = ['swissScoreboard', compId, stageId];
  const { data: scoreboard } = useQuery({
    queryKey: scoreboardKey,
    queryFn: () => api.getSwissScoreboard(compId, stageId),
    enabled: !!compId && !!stageId,
  });

  // Same queryKey MatchesSection (AdminPanel.tsx) / SeedingRoundsPanel share
  // — rides the existing cache, gives this panel each match's resolvedSeries
  // for the inline ResultEntryForm.
  const matchesKey = ['matches', compId];
  const { data: matches } = useQuery({
    queryKey: matchesKey,
    queryFn: () => api.matches(compId),
    enabled: !!compId,
  });
  const matchById = new Map((matches ?? []).map((m) => [m.sk, m]));

  const [selectedRound, setSelectedRound] = useState<number | null>(null);
  const [expandedMatchId, setExpandedMatchId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const rounds = scoreboard?.rounds ?? [];
  const currentRound = scoreboard?.currentRound ?? 0;
  const activeRound = selectedRound ?? currentRound;
  const activeRoundData = rounds.find((r) => r.round === activeRound);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: scoreboardKey });
    qc.invalidateQueries({ queryKey: matchesKey });
  };

  const completeMut = useMutation({
    mutationFn: () => api.completeSwissRound(compId),
    onSuccess: () => { setError(''); invalidateAll(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const pairMut = useMutation({
    mutationFn: () => api.pairSwissRound(compId),
    onSuccess: () => { setError(''); setSelectedRound(null); invalidateAll(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (!scoreboard || !scoreboard.initialised) return null;

  const complete = scoreboard.summary?.complete ?? false;
  const currentRoundRow = rounds.find((r) => r.round === currentRound);
  // The route's own guard (POST .../swiss/pair refuses until the round it's
  // pairing FROM is complete) is what actually enforces this — roundComplete
  // just keeps the button from firing an avoidable 409.
  const pairDisabledReason = complete
    ? 'Swiss is complete — no further rounds to pair.'
    : !scoreboard.roundComplete
      ? `Confirm every match in round ${currentRound} first.`
      : currentRoundRow?.status === 'COMPLETE'
        ? null
        : 'Complete the current round before pairing the next one.';

  return (
    <div className="mt-3 border-t border-white/5 pt-5">
      <p className="mb-2 font-display text-[10px] font-black uppercase tracking-wider text-ink-meta">Swiss Rounds</p>
      <HelpText>
        <p className="mb-2 text-[11px] text-ink-meta">
          Every round pairs teams within the same win-loss record (never a repeat opponent, odd records get a
          bye). Enter each match&apos;s result below, Complete the round once every match has one, then Pair the
          next round — teams crossing the win threshold advance, the loss threshold eliminates.
        </p>
      </HelpText>

      {complete && (
        <p className="mb-3 text-sm text-ink-primary">
          Swiss complete — {scoreboard.summary!.advanced.length} advance.
        </p>
      )}

      {/* Standings — typography-only (house doctrine: no colour strips). */}
      <div className="mb-3 overflow-x-auto">
        <table className="w-full min-w-[420px] text-xs">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-ink-meta">
              <th className="w-8 py-1">Pos</th>
              <th className="py-1">Team</th>
              <th className="w-14 py-1 text-center">W-L</th>
              <th className="w-16 py-1 text-center">Buchholz</th>
              <th className="w-16 py-1 text-right">State</th>
            </tr>
          </thead>
          <tbody>
            {scoreboard.standings.map((s, i) => (
              <tr key={s.id} className="border-t border-white/[0.05]">
                <td className="py-1.5 text-ink-meta">{i + 1}</td>
                <td className="py-1.5 text-ink-primary">{displayClanName(s.id)}</td>
                <td className="py-1.5 text-center text-ink-secondary">{`${s.wins}-${s.losses}`}</td>
                <td className="py-1.5 text-center text-ink-secondary">{s.buchholz}</td>
                <td className={`py-1.5 text-right font-semibold ${s.state === 'IN' ? 'text-ink-primary' : s.state === 'OUT' ? 'text-ink-meta' : 'text-ink-secondary'}`}>
                  {s.state}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Round tabs */}
      {rounds.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {rounds.map((r) => (
            <button
              key={r.round}
              type="button"
              onClick={() => setSelectedRound(r.round)}
              className={`rounded-md px-2.5 py-1 text-xs transition ${
                activeRound === r.round
                  ? 'bg-accent-500/15 text-accent-500'
                  : 'bg-white/[0.05] text-ink-secondary hover:bg-white/[0.08]'
              }`}
            >
              Round {r.round}
            </button>
          ))}
        </div>
      )}

      {/* Active round's matches */}
      {activeRoundData && (
        <div className="mb-3 space-y-1.5">
          {activeRoundData.pairings.map((p: SwissScoreboardPairing, i) => {
            if (p.teamB === null) {
              return (
                <div key={i} className="rounded-lg bg-white/[0.03] px-3 py-2 text-xs text-ink-meta">
                  <span className="text-ink-secondary">{displayClanName(p.teamA)}</span> — bye this round (free win)
                </div>
              );
            }
            const match = p.matchId ? matchById.get(p.matchId) : undefined;
            const isExpanded = expandedMatchId === p.matchId;
            const confirmed = p.status === 'CONFIRMED';
            return (
              <div key={i} className="overflow-hidden rounded-lg bg-white/[0.04]">
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                    <span className="truncate text-ink-primary">{displayClanName(p.teamA)}</span>
                    <span className="shrink-0 text-xs text-ink-meta">vs</span>
                    <span className="truncate text-ink-primary">{displayClanName(p.teamB)}</span>
                    <span className="shrink-0 text-[10px] text-ink-meta">({p.bucket})</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {p.winner && <span className="text-[11px] text-ink-secondary">Winner: {displayClanName(p.winner)}</span>}
                    {p.status && <StatusBadge status={p.status} />}
                    {p.matchId && (
                      <button
                        type="button"
                        onClick={() => setExpandedMatchId(isExpanded ? null : p.matchId!)}
                        className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-accent-500 transition-colors hover:bg-white/[0.06]"
                      >
                        <Pencil className="h-3 w-3" /> {isExpanded ? 'Cancel' : confirmed ? 'Edit result' : 'Enter result'}
                      </button>
                    )}
                  </div>
                </div>
                {isExpanded && p.matchId && (
                  <div className="border-t border-white/[0.06] px-3 py-2.5">
                    <ResultEntryForm
                      matchId={p.matchId}
                      compId={compId}
                      clan1={p.teamA}
                      clan2={p.teamB}
                      resolvedSeries={match?.resolvedSeries}
                      games={match?.games}
                      onSaved={() => { setExpandedMatchId(null); invalidateAll(); }}
                      onCancel={() => setExpandedMatchId(null)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!complete && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={currentRoundRow?.status === 'COMPLETE' || !scoreboard.roundComplete || completeMut.isPending}
            onClick={() => completeMut.mutate()}
            className="btn-primary px-2.5 py-1 text-xs disabled:opacity-40"
          >
            {completeMut.isPending ? 'Completing…' : `Complete round ${currentRound}`}
          </button>
          <button
            type="button"
            disabled={!!pairDisabledReason || pairMut.isPending}
            onClick={() => pairMut.mutate()}
            className="btn-ghost px-2.5 py-1 text-xs disabled:opacity-40"
          >
            {pairMut.isPending ? 'Pairing…' : `Pair round ${currentRound + 1}`}
          </button>
          {pairDisabledReason && <span className="text-[11px] text-ink-meta">{pairDisabledReason}</span>}
        </div>
      )}

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
}
